import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Bubble, Icon, notification, Popover, Radio, Switch } from 'tea-component';
import { connect, useIntl } from "umi";
import classNames from "classnames";
import {
  checkAllConditions,
  checkRawColumnModeConditions,
  getChartTypeInfos
} from "@/pages/datalab/pages/Multidimensional/utils";
import { LANGUAGE_EN } from "@home/_config/config";
import {allowChartTypeShowUserFilter, allowShowUserFilter} from '@dbl/core/global';
import { DATALAB_ANALYSIS_ERROR_CODE } from '@dbl/core/global';
import { ChartType, type TChartType } from '@dbl/core/chart';
import {
  ChartGroupMeta,
  ChartTypeInfos,
  SwitchChartTypes,
  DEFAULT_CHART_GROUP_KEY,
} from '@dbl/core/chartEditor';
import { CustomColumn } from '@/pages/datalab/pages/Multidimensional/Analysis/interface';
import { ChartCalculateMode, type TChartCalculateMode } from '@dbl/core/chartEditor';
import type { IColumnInfo } from '@dbl/core/dataSource';
import { DEFAULT_NAME, DEFAULT_NAME_CN, DEFAULT_NAME_EN } from '@/pages/datalab/components/UserFilter/FilterPanel/constant';
import { namespace } from "@/pages/datalab/pages/Multidimensional/Analysis/model";
import { useFilterPanalData } from '@/pages/datalab/components/UserFilter/FilterPanel/hooks';
import SvgIcon from "@home/components/SvgIcon";
import { useChartSettingsDispatch, useChartSettingsSelector } from '../ChartSettingsV2/context';
import { buildCustomizeSettingForApi } from '../ChartSettingsV2/services/api-upload';
import { applyChartTypeLinkage } from '../ChartSettingsV2/linkage/chartTypeSwitch/applyChartTypeLinkage';
import type { IPrepareConfigOutput } from '../ChartSettingsV2/linkage/chartTypeSwitch/prepareConfigForChartType';
import TipContext from "@home/components/TipContextComponents/TipContext";
import FormatHtmlTextToReact from '@home/components/FormatHtmlTextToReact';
import './index.scss';
// 这些图表互相之间不需要重新发起请求;
// const NotReCalculateChartType = [ChartType.Table, ChartType.Pie, ChartType.StackBar, ChartType.Line, ChartType.BigNumber, ChartType.Trend, ChartType.EventLine];

const BUTTON_GAP = 6;
const DEFAULT_BUTTON_WIDTH = 38;
const DEFAULT_ELLIPSIS_WIDTH = 40;
const MODE_MARGIN_LEFT = 20;
const USER_FILTER_MARGIN_LEFT = 20;

function SwitchChartType(props: any) {
  const {
    gameCode,
    columnFilters,
    columnUserFilters,
    columnCompares,
    columnMetrics,
    columnDimensions,
    columnShowDimensions,
    columnRawColumns,
    columnX,
    chartType,
    mode,
    analysisRunning,
    dataTables,
    saveColumnInfos,
    hasUserFilters,
    dispatch,
  } = props;
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const isInitFieldData = useRef<boolean>(false);
  const { fieldLoading, getFieldData } = useFilterPanalData({ gameCode });
  const baseFromContext = useChartSettingsSelector((s) => s.base);
  const draftFromContext = useChartSettingsSelector((s) => s.draft);
  const hasDraftFromContext = useChartSettingsSelector((s) => s.hasDraft);
  const chartSettingsDispatch = useChartSettingsDispatch();

  const handleOnRefreshType = useCallback((
    type: string,
    prepared: IPrepareConfigOutput,
  ) => {
    const nextChartType = type as TChartType;

    const customizeSetting = buildCustomizeSettingForApi({
      chartType: nextChartType,
      base: prepared.base,
      draft: prepared.draft,
      hasDraft: prepared.hasDraft,
      columnDimensions,
      columnMetrics,
      mode,
    });

    dispatch({
      type: `${namespace}/getDataAnlysis`,
      payload: {
        isEn,
        customizeSetting,
        game_code: gameCode,
        // newFlag: true,
      },
      callback: (payload: any) => {
        if (payload?.errorMsg && payload?.errorCode !== 40005) {
          notification.error({
            className: 'dbl-analysis-msg',
            description: DATALAB_ANALYSIS_ERROR_CODE?.includes(payload?.errorCode) ? formatMessage({ id: `dbl_error_${payload?.errorCode || '1'}` }, { name: payload?.errorData, msg: payload?.errorMsg }) : payload?.errorMsg,
            duration: 3000,
          });
        }
        dispatch({
          type: `${namespace}/save`,
          payload: {
            ...(payload || {}),
            analysisRunning: false,
            analysisReqId: '',
            dragState: {
              currentDrag: '',
              errorDrag: {},
            },
          },
        });
      },
    });
  }, [
    columnDimensions,
    columnMetrics,
    dispatch,
    formatMessage,
    gameCode,
    isEn,
    mode,
  ]);

  const infos = useMemo(() => getChartTypeInfos(), []);
  const showUserFilter = useMemo(() => allowShowUserFilter(gameCode), [gameCode]);
  const tableIds = useMemo(() => dataTables?.map((item: { table_id: any; }) => item?.table_id) || [], [dataTables]);
  const orderedTypes = useMemo(() => SwitchChartTypes, []);
  const groupedTypes = useMemo(() => {
    const groupList: Array<{ key: string; nameCn: string; nameEn: string; types: TChartType[]; }> = [];
    const groupMap = new Map<string, number>();
    const defaultMeta = ChartGroupMeta[DEFAULT_CHART_GROUP_KEY];
    orderedTypes.forEach((type) => {
      const info = ChartTypeInfos[type] || {};
      const rawGroupKey = info.group || DEFAULT_CHART_GROUP_KEY;
      const groupKey = typeof rawGroupKey === 'string' ? rawGroupKey : DEFAULT_CHART_GROUP_KEY;
      const { nameCn, nameEn } = ChartGroupMeta[groupKey] || defaultMeta;

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, groupList.length);
        groupList.push({
          key: groupKey,
          nameCn: nameCn || defaultMeta.nameCn,
          nameEn: nameEn || defaultMeta.nameEn,
          types: [],
        });
      }
      const index = groupMap.get(groupKey) as number;
      groupList[index].types.push(type);
    });

    const selectedType = chartType as TChartType | undefined;
    if (!selectedType) {
      return groupList;
    }
    const selectedGroupIndex = groupList.findIndex(({ types }) => types.includes(selectedType));
    if (selectedGroupIndex <= 0) {
      return groupList;
    }
    const [selectedGroup] = groupList.splice(selectedGroupIndex, 1);
    groupList.unshift(selectedGroup);
    return groupList;
  }, [orderedTypes, chartType]);

  const [displayTypes, setDisplayTypes] = useState<{ visible: TChartType[]; overflow: TChartType[]; }>({
    visible: SwitchChartTypes,
    overflow: [],
  });
  const [overflowVisible, setOverflowVisible] = useState(false);
  const chartTypeRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const chartTypeRefCallbacks = useRef<Record<string, (instance: HTMLDivElement | null) => void>>({});
  const containerRef = useRef<HTMLDivElement | null>(null);
  const typesWrapperRef = useRef<HTMLDivElement | null>(null);
  const ellipsisRef = useRef<HTMLDivElement | null>(null);
  const modeRef = useRef<HTMLDivElement | null>(null);
  const userFilterRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [typesWrapperWidth, setTypesWrapperWidth] = useState<number>(0);

  const getChartTypeRef = useCallback((type: TChartType) => {
    if (!chartTypeRefCallbacks.current[type]) {
      chartTypeRefCallbacks.current[type] = (node: HTMLDivElement | null) => {
        chartTypeRefs.current[type] = node;
      };
    }
    return chartTypeRefCallbacks.current[type];
  }, []);

  const handleSelectChartType = useCallback((type: TChartType) => {
    if (analysisRunning) return;

    const { metric } = ChartTypeInfos[type];
    let newMetrics = columnMetrics;
    if (metric.operator === '===' || metric.operator === '<=') {
      newMetrics = columnMetrics.slice(0, metric.count);
    }

    const { isCheckSuccess } = ((type === ChartType.Table) && (mode === ChartCalculateMode.RawColumn)) ? checkRawColumnModeConditions(type, { columnFilters, columnRawColumns }, isEn) : checkAllConditions(type, {
      columnFilters,
      columnCompares,
      columnMetrics: newMetrics,
      columnDimensions,
      columnX,
      columnShowDimensions,
    }, isEn);

    dispatch({
      type: `${namespace}/save`,
      payload: {
        rowLimit: 5000,
        chartType: type,
        hadAnalysed: false,
        analysisRunning: false,
        analysisReqId: '',
        columnMetrics: newMetrics,
        table: undefined,
        chartData: {},
      },
    });

    const prepared = applyChartTypeLinkage(chartSettingsDispatch, {
      chartType: type,
      base: baseFromContext,
      draft: draftFromContext,
      hasDraft: hasDraftFromContext,
      columnDimensions,
      columnMetrics: newMetrics,
      mode,
    });

    if (isCheckSuccess && (type !== ChartType.Table)) {
      handleOnRefreshType(type, prepared);
    }
    
  }, [analysisRunning, columnCompares, columnDimensions, columnFilters, columnMetrics, columnRawColumns, columnShowDimensions, columnX, handleOnRefreshType, isEn, mode, dispatch, chartSettingsDispatch, baseFromContext, draftFromContext, hasDraftFromContext]);

  const getAvailableWidth = useCallback(() => {
    const containerWidth = containerRef.current?.clientWidth || 0;
    if (!containerWidth) {
      setTypesWrapperWidth((prev) => (prev === 0 ? prev : 0));
      return 0;
    }
    let reservedWidth = 0;
    if (chartType === ChartType.Table) {
      const modeWidth = modeRef.current?.offsetWidth || 0;
      if (modeWidth) {
        reservedWidth += modeWidth + MODE_MARGIN_LEFT;
      }
      if (showUserFilter) {
        const userFilterWidth = userFilterRef.current?.offsetWidth || 0;
        if (userFilterWidth) {
          reservedWidth += userFilterWidth + USER_FILTER_MARGIN_LEFT;
        }
      }
    }
    const nextWidth = Math.max(containerWidth - reservedWidth, 0);
    setTypesWrapperWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    return nextWidth;
  }, [chartType, showUserFilter]);

  const calculateDisplayState = useCallback(() => {
    const availableWidth = getAvailableWidth();
    const wrapperWidth = availableWidth || typesWrapperRef.current?.clientWidth || 0;
    if (!wrapperWidth) {
      setDisplayTypes({
        visible: orderedTypes,
        overflow: [],
      });
      return;
    }

    const widthMap = new Map<TChartType, number>();
    orderedTypes.forEach((type) => {
      const rectWidth = chartTypeRefs.current[type]?.offsetWidth || DEFAULT_BUTTON_WIDTH;
      widthMap.set(type, rectWidth + BUTTON_GAP);
    });

    const visible: TChartType[] = [];
    const overflow: TChartType[] = [];
    let usedWidth = 0;

    orderedTypes.forEach((type) => {
      const currentWidth = widthMap.get(type) || DEFAULT_BUTTON_WIDTH;
      if (usedWidth + currentWidth > wrapperWidth) {
        overflow.push(type);
      } else {
        visible.push(type);
        usedWidth += currentWidth;
      }
    });

    if (overflow.length) {
      const ellipsisWidth = (ellipsisRef.current?.offsetWidth || DEFAULT_ELLIPSIS_WIDTH) + BUTTON_GAP;
      while (visible.length && usedWidth + ellipsisWidth > wrapperWidth) {
        const removed = visible.pop() as TChartType;
        overflow.unshift(removed);
        usedWidth -= widthMap.get(removed) || DEFAULT_BUTTON_WIDTH;
      }
      if (!visible.length && overflow.length) {
        visible.push(overflow.shift() as TChartType);
      }
    }

    setDisplayTypes({
      visible,
      overflow,
    });
  }, [getAvailableWidth, orderedTypes]);

  useLayoutEffect(() => {
    calculateDisplayState();
  }, [calculateDisplayState, orderedTypes, isEn]);

  useEffect(() => {
    calculateDisplayState();
  }, [calculateDisplayState, showUserFilter, hasUserFilters, mode]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }
    if (containerRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        calculateDisplayState();
      });
      resizeObserverRef.current.observe(containerRef.current);
    }
    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
  }, [calculateDisplayState]);

  useEffect(() => {
    if (!displayTypes.overflow.length) {
      setOverflowVisible(false);
    }
  }, [displayTypes.overflow.length]);

  // 开启关闭用户筛选器
  const handleSwitchUserFilter = async (value: boolean) => {
    if (!gameCode || !tableIds?.length) {
      return notification.warning({
        description: formatMessage({ id: 'dbl_filter_user_filter_invalid' }),
      });
    }
    // 开启用户筛选器，获取字段数据
    if (value) {
      try {
        const { error } = await getFieldData(gameCode, tableIds);
        isInitFieldData.current = true;
        if (error) return;
      } catch (err) {
        throw err;
      }
    }
    const newColumnUserFilters = columnUserFilters?.length ? columnUserFilters : [
      {
        id: `group_${uuidv4()}`,
        name: DEFAULT_NAME,
        name_en: DEFAULT_NAME_EN,
        name_cn: DEFAULT_NAME_CN,
        logical_condition: 'AND',
        is_all_users: true,
        isNameCustomized: false,
        filters: [
          {
            id: `filter_${uuidv4()}`,
            field: undefined,
            fieldType: undefined,
            operator: undefined,
            value: undefined,
            hasError: true,
          }
        ],
        logic: 'AND',
        summaryEn: DEFAULT_NAME_EN,
        summaryCn: DEFAULT_NAME_CN,
      }
    ];
    
    dispatch({
      type: `${namespace}/save`,
      payload: {
        hasUserFilters: value,
        columnUserFilters: newColumnUserFilters,
      }
    });
    
    // ChartSettingsV2 internal handles user filter changes
  };

  // 初始化字段数据
  const initFieldData = async () => {
    const { error } = await getFieldData(gameCode, tableIds);
    isInitFieldData.current = true;
    // 报错关闭用户筛选器
    if (error) {
      dispatch({
        type: `${namespace}/save`,
        payload: {
          hasUserFilters: false,
        }
      });
    }
  };

  // 获取字段信息
  useEffect(() => {
    if (!allowChartTypeShowUserFilter({chartType, mode}) || !showUserFilter || !gameCode || !tableIds?.length || !hasUserFilters || isInitFieldData.current) return;
    initFieldData();
  }, [gameCode, isInitFieldData, hasUserFilters, tableIds, showUserFilter, chartType, mode]);

  return (<div className={'dbl-switch-chart'} ref={containerRef}>
    <div
      className='dbl-switch-chart-types-wrapper'
      ref={typesWrapperRef}
      style={typesWrapperWidth ? { maxWidth: `${typesWrapperWidth}px` } : undefined}
    >
      <div className='dbl-switch-chart-types'>
        {
          displayTypes.visible.map((type) => {
            const { icon }: any = ChartTypeInfos[type];
            return (
              <Bubble
                style={{ maxWidth: 402 }}
                key={type}
                dark
                content={(
                  <FormatHtmlTextToReact
                    text={infos[type]?.[isEn ? 'en' : 'cn'] || ''}
                    style={{ lineHeight: '20px' }}
                  />
                )}
              >
                <div
                  ref={getChartTypeRef(type)}
                  className={classNames('dbl-switch-chart-type', {
                    active: chartType === type,
                    disabled: analysisRunning
                  })}
                  onClick={() => {
                    handleSelectChartType(type);
                  }}
                >
                  <SvgIcon type={icon} />
                </div>
              </Bubble>
            );
          })
        }
        {
          !!displayTypes.overflow.length && (
            <Popover
              trigger={'click'}
              placement='bottom-start'
              closeOnScroll={true}
              visible={overflowVisible}
              onVisibleChange={(visible) => setOverflowVisible(visible)}
              overlay={(
                <div className='dbl-switch-chart-overflow'>
                  {
                    groupedTypes.map(({ key, nameCn, nameEn, types }) => (
                      <div className='dbl-switch-chart-overflow-group' key={`overflow-group-${key}`}>
                        <div className='dbl-switch-chart-overflow-group-title'>
                          {isEn ? nameEn : nameCn}
                        </div>
                        <div className='dbl-switch-chart-overflow-group-list'>
                          {
                            types.map((type) => {
                              const { icon }: any = ChartTypeInfos[type];
                              return (
                                <Bubble
                                  key={`overflow-${type}`}
                                  style={{ maxWidth: 402 }}
                                  placement='top'
                                  content={(
                                    <FormatHtmlTextToReact
                                      text={infos[type]?.[isEn ? 'en' : 'cn'] || ''}
                                      style={{ lineHeight: '20px' }}
                                    />
                                  )}
                                >
                                  <div
                                    className={classNames('dbl-switch-chart-type', 'dbl-switch-chart-type-overflow', {
                                      active: chartType === type,
                                      disabled: analysisRunning
                                    })}
                                    onClick={() => {
                                      handleSelectChartType(type);
                                      setOverflowVisible(false);
                                    }}
                                  >
                                    <SvgIcon type={icon} />
                                  </div>
                                </Bubble>
                              );
                            })
                          }
                        </div>
                      </div>
                    ))
                  }
                </div>
              )}
              overlayClassName='dbl-switch-chart-overflow-pop'
            >
              <div
                ref={ellipsisRef}
                className={classNames('dbl-switch-chart-type', 'dbl-switch-chart-ellipsis')}
              >
                <SvgIcon type={'there-points'} />
              </div>
            </Popover>
          )
        }
      </div>
    </div>
    {
      chartType === ChartType.Table ? (
        <div ref={modeRef} className='dbl-switch-chart-mode-wrapper'>
          <Radio.Group
            className={classNames('dbl-switch-chart-mode', { 'dbl-switch-chart-mode-cn': !isEn })}
            value={mode}
            onChange={(value) => {
              try {
                dispatch({
                  type: `${namespace}/save`,
                  payload: { rowLimit: 5000 },
                });
                if ((value === ChartCalculateMode.RawColumn) && (dataTables?.length > 0)) {
                  let newRawColumns: IColumnInfo[] = [];
                  if (columnRawColumns.length === 0) {
                    dataTables.forEach(({ column_class_list }: { column_class_list: { columns: IColumnInfo[]; }[]; }) => {
                      column_class_list.forEach((item: { columns: IColumnInfo[]; }) => {
                        item.columns.forEach(column => newRawColumns.push({ ...column }));
                      });
                    });
                  } else {
                    newRawColumns = columnRawColumns;
                  }

                  dispatch({
                    type: `${namespace}/switchChartCalculateMode`,
                    payload: {
                      mode: value,
                      columnRawColumns: newRawColumns,
                      save_column_infos: {},
                      chartData: {},
                      table: undefined,
                      hadAnalysed: false,
                    }
                  });

                } else {
                  dispatch({
                    type: `${namespace}/switchChartCalculateMode`,
                    payload: {
                      mode: value,
                      save_column_infos: {},
                      chartData: {},
                      table: undefined,
                      hadAnalysed: false,
                    }
                  });
                }
              } catch (e) {
                console.error('表格模式切换错误: ', e);
              }
            }}
            disabled={analysisRunning}
          >
            {/* <span className={'label-name'}>{formatMessage({ id: 'dbl_multi_query_mode' })}</span> */}
            <Radio name={ChartCalculateMode.Aggregate}>{formatMessage({ id: 'dbl_multi_aggregation' })}</Radio>
            <Radio name={ChartCalculateMode.RawColumn}>
              {formatMessage({ id: 'dbl_multi_detail' })}
            </Radio>
            <TipContext
              stype={{ maxWidth: 400 }}
              tipText={formatMessage({ id: 'dbl_multi_query_mode_info' })}
              arrowPointAtCenter
              placement={"right"}
            />
          </Radio.Group>
        </div>
      ) : ''
    }
    {
      allowChartTypeShowUserFilter({chartType, mode}) && showUserFilter ? <div className='dbl-switch-chart-user-filter' ref={userFilterRef}>
        <Switch
          value={hasUserFilters}
          onChange={(value) => {
            handleSwitchUserFilter(value);
          }}
        >
          {fieldLoading ? <Icon type="loading" /> : formatMessage({ id: 'dbl_filter_user_filters' })}
        </Switch>
      </div> : ""
    }
  </div>);
}

export default connect((state: any) => {
  const {
    customColumns,
    columnFilters = [],
    columnUserFilters = [],
    columnCompares = [],
    columnMetrics = [],
    columnDimensions = [],
    columnShowDimensions = [],
    columnRawColumns = [],
    columnX = [],
    mode,
    chartType = 'Table',
    analysisRunning,
    dataTables = [],
    hashEqualForAnalysis,
    hadAnalysed,
    hasUserFilters,
    save_column_infos: saveColumnInfos,
  }: {
    customColumns: CustomColumn[];
    columnFilters: IColumnInfo[];
    columnUserFilters: IColumnInfo[];
    columnCompares: IColumnInfo[];
    columnMetrics: IColumnInfo[];
    columnDimensions: IColumnInfo[];
    columnShowDimensions: IColumnInfo[];
    columnRawColumns: IColumnInfo[];
    columnX: IColumnInfo[];
    mode: TChartCalculateMode;
    chartType: string;
    analysisRunning: boolean;
    dataTables: [];
    save_column_infos: any;
    hashEqualForAnalysis: boolean;
    hadAnalysed: boolean;
    hasUserFilters: boolean;
  } = state[namespace];
  return {
    customColumns,
    columnFilters,
    columnUserFilters,
    columnCompares,
    columnMetrics,
    columnDimensions,
    columnShowDimensions,
    columnRawColumns,
    columnX,
    mode,
    chartType,
    analysisRunning,
    dataTables,
    saveColumnInfos,
    hashEqualForAnalysis,
    hadAnalysed,
    hasUserFilters,
  };
})(SwitchChartType);